import { IndexedDBCache } from 'finhelper';
import moment from 'moment';

import { buildFunnelChartClientsQuery } from '#app/api/funnel/buildFunnelChartClientsQuery';
import {
    buildFunnelCrmClientServicesQuery,
    extractFunnelScopedClientIdsArray,
} from '#app/api/funnel/buildFunnelCrmClientServicesQuery';
import DateConstants from '#app/shared/lib/dates/DateConstants';

import type { ApiResponse } from '../types/api.types';

export type FunnelActivesRatingsServiceItemRow = Record<string, number | string>;

const MAIN_STATS_CACHE_KEY = 'main_stats';

const laravelAll = <T>(query: {
    all: (ok: (r: T) => void, err?: (e: unknown) => void) => void;
}): Promise<T> =>
    new Promise((resolve, reject) => {
        query.all(
            (response) => resolve(response),
            (error) => reject(error)
        );
    });

const buildServiceItemsFromRows = (
    rows: unknown[]
): FunnelActivesRatingsServiceItemRow[] => {
    const byMonth: Record<string, FunnelActivesRatingsServiceItemRow> = {};

    rows.forEach((raw) => {
        const item = raw as {
            client_id?: string | number;
            is_paid?: boolean;
            amount?: string | number;
            service?: { id?: string | number };
            created_at?: string;
        };
        if (!item.is_paid || !item.service || item.amount == null) {
            return;
        }
        const clientId = String(item.client_id ?? '');
        if (!clientId) {
            return;
        }

        let date = moment(item.created_at, DateConstants.dateTimeUserFormat);
        if (!date.isValid()) {
            date = moment(item.created_at, DateConstants.dateTimeFormat);
        }
        if (!date.isValid()) {
            return;
        }

        const monthKey = date.startOf('month').format('DD-MM-YYYY');
        if (!byMonth[monthKey]) {
            byMonth[monthKey] = { date: monthKey };
        }
        const amount = parseFloat(String(item.amount)) || 0;
        if (amount <= 0) {
            return;
        }
        const prev = Number(byMonth[monthKey][clientId]) || 0;
        byMonth[monthKey][clientId] = prev + amount;
    });

    return Object.values(byMonth);
};

const fetchCrmClientServicesRows = async (): Promise<unknown[]> => {
    const cached = await IndexedDBCache.get(MAIN_STATS_CACHE_KEY);
    const cachedPayload = cached as { serviceData?: unknown[]; clientsData?: unknown[] } | null;
    const cachedRows = cachedPayload?.serviceData;
    if (Array.isArray(cachedRows) && cachedRows.length > 0) {
        return cachedRows;
    }

    let scopedClientIdsArray = extractFunnelScopedClientIdsArray(cachedPayload?.clientsData);
    if (scopedClientIdsArray.length === 0) {
        const clientsResponse = await laravelAll<ApiResponse<unknown[]>>(
            buildFunnelChartClientsQuery(true)
        );
        scopedClientIdsArray = extractFunnelScopedClientIdsArray(clientsResponse.data);
    }

    if (scopedClientIdsArray.length === 0) {
        return [];
    }

    const serviceResponse = await laravelAll<ApiResponse<unknown[]>>(
        buildFunnelCrmClientServicesQuery(true, scopedClientIdsArray)
    );

    return Array.isArray(serviceResponse.data) ? serviceResponse.data : [];
};

let serviceItemsInFlight: Promise<FunnelActivesRatingsServiceItemRow[]> | null =
    null;

/**
 * Месячные суммы выручки по client_id для calculateServiceTotals.
 * Один in-flight на вкладку; при наличии — reuse IndexedDB `main_stats.serviceData`.
 */
export const loadFunnelActivesRatingsServiceItems = async (): Promise<
    FunnelActivesRatingsServiceItemRow[]
> => {
    if (!serviceItemsInFlight) {
        serviceItemsInFlight = fetchCrmClientServicesRows()
            .then((rows) => buildServiceItemsFromRows(rows))
            .finally(() => {
                serviceItemsInFlight = null;
            });
    }
    return serviceItemsInFlight;
};
